'use client';
import * as React from 'react';
import clsx from 'clsx';
import { styled } from '@mui/material/styles';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import type { CalendarView } from '@mui/x-scheduler-internals/models';
import { useEventCalendarStyledContext } from '../../EventCalendarStyledContext';

const ViewSwitcherRoot = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'ViewSwitcher',
})({
  display: 'flex',
});

export interface ViewSwitcherProps extends React.HTMLAttributes<HTMLDivElement> {
  views: CalendarView[];
  view: CalendarView;
  onViewChange: (view: CalendarView, event: Event) => void;
}

type ViewSwitcherComponent = (
  props: ViewSwitcherProps & {
    ref?: React.ForwardedRef<HTMLDivElement>;
  },
) => React.ReactElement | null;

export const ViewSwitcher = React.forwardRef(function ViewSwitcher(
  props: ViewSwitcherProps,
  forwardedRef: React.ForwardedRef<HTMLDivElement>,
) {
  const { views, onViewChange, view, className, ...other } = props;

  const { schedulerId, classes, localeText } = useEventCalendarStyledContext();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (event: React.MouseEvent<HTMLElement>, newView: CalendarView) => {
    onViewChange(newView, event.nativeEvent);
    handleMenuClose();
  };

  return (
    <ViewSwitcherRoot
      ref={forwardedRef}
      {...other}
      className={clsx(className, classes.viewSwitcher)}
    >
      <Button
        className={classes.viewSwitcherButton}
        size="medium"
        id={`${schedulerId}-view-switcher-button`}
        aria-controls={open ? `${schedulerId}-view-switcher-menu` : undefined}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={handleMenuOpen}
        endIcon={<ExpandMoreOutlined />}
      >
        {localeText[view]}
      </Button>
      <Menu
        className={classes.viewSwitcherMenu}
        id={`${schedulerId}-view-switcher-menu`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        slotProps={{
          list: {
            'aria-labelledby': `${schedulerId}-view-switcher-button`,
          },
        }}
      >
        {views.map((viewItem) => (
          <MenuItem
            key={viewItem}
            selected={view === viewItem}
            role="menuitemradio"
            aria-checked={view === viewItem}
            onClick={(event) => handleMenuItemClick(event, viewItem)}
          >
            {localeText[viewItem]}
          </MenuItem>
        ))}
      </Menu>
    </ViewSwitcherRoot>
  );
}) as ViewSwitcherComponent;
